import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges, SimpleChanges, ViewChild, inject} from '@angular/core';
import { CommonModule } from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import {forkJoin, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
  Era5DayWeatherForecastService,
  IbmDayWeatherForecastService
} from '@6grain/masofa-monolith-api-client/weather-report';
import {UgmWeatherDataService} from '@6grain/masofa-monolith-api-client/weather';
import { MsfSelect } from '@components/select/select';

export interface ForecastDay {
  date: string;
  tempMin: number;
  tempMax: number;
  condition: string;
  conditionKey: string; // Raw key for icon lookup
  precipitation: number;
  humidity: number;
  windSpeed: number;
  windDirection: number;
  iconPath?: string; // Path to the weather icon
}

@Component({
  selector: 'app-weather-summary-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule, SelectModule, FormsModule, MsfSelect],
  templateUrl: './weather-summary-tab.component.html',
  styleUrls: ['./weather-summary-tab.component.scss']
})
export class WeatherSummaryTabComponent implements OnChanges, AfterViewInit {
  private era5DayWeatherForecastService = inject(Era5DayWeatherForecastService);
  private ugmWeatherDataService = inject(UgmWeatherDataService);
  private ibmDayWeatherForecastService = inject(IbmDayWeatherForecastService);
  private translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() latitude: number | null = null;
  @Input() longitude: number | null = null;
  @Input() dataSourceOptions?: any[];

  @ViewChild('forecastScroll') forecastScrollElement!: ElementRef;

  forecastDays: ForecastDay[] = [];
  isLoading: boolean = false;
  selectedDataSource: string = 'era5';
  showForecastPrev = false;
  showForecastNext = false;

  ngAfterViewInit(): void {
    this.updateForecastNavigationVisibility();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['latitude'] || changes['longitude']) && this.latitude !== null && this.longitude !== null) {
      this.loadWeatherInfo();
    }
  }

  onDataSourceChange(): void {
    this.loadWeatherInfo();
  }

  private loadWeatherInfo(): void {
    if (this.latitude === null || this.longitude === null) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    // Choose the appropriate data loading method based on data source
    switch (this.selectedDataSource) {
      case 'era5':
        this.loadEra5ForecastData();
        break;
      case 'ugm':
        this.loadUgmForecastData();
        break;
      case 'ibm':
        this.loadIbmForecastData();
        break;
      default:
        this.loadEra5ForecastData();
    }
  }

  /**
   * Load ERA5 forecast data for next 6 days
   */
  private loadEra5ForecastData(): void {
    const today = new Date();
    const requests = [];

    for (let i = 0; i < 6; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      date.setUTCHours(0, 0, 0, 0);
      const dateStr = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD

      const request = this.era5DayWeatherForecastService.weatherReportsEra5DayWeatherForecastGetByCoordinatesAndDatePost({
        latitude: this.latitude!,
        longitude: this.longitude!,
        inputDate: dateStr
      });

      requests.push(
        request.pipe(
          catchError(error => {
            console.error(`Error loading ERA5 forecast for ${dateStr}:`, error);
            return of(null);
          })
        )
      );
    }

    forkJoin(requests).pipe(
      map(results => {
        this.forecastDays = results
          .filter(item => item !== null)
          .map((item: any) => {
            const avgTemp = (item.temperatureMin + item.temperatureMax) / 2;
            const weatherConditionKey = this.getWeatherCondition(
              item.fallout,
              avgTemp,
              item.humidity
            );

            return {
              date: item.date,
              tempMin: Math.round(item.temperatureMin),
              tempMax: Math.round(item.temperatureMax),
              condition: this.translate.instant(weatherConditionKey),
              conditionKey: weatherConditionKey,
              precipitation: Math.round((item.fallout || 0) * 10) / 10,
              humidity: Math.round(item.humidity || 0),
              windSpeed: Math.round((item.windSpeed || 0) * 10) / 10,
              windDirection: Math.round(item.windDerection || 0),
              iconPath: this.getWeatherIconPath(weatherConditionKey)
            };
          });
        return results;
      }),
      catchError(error => {
        console.error('Error loading ERA5 forecast data:', error);
        this.isLoading = false;
        this.cdr.markForCheck();
        return of(null);
      })
    ).subscribe(() => {
      this.isLoading = false;
      this.scheduleForecastNavigationUpdate();
      this.cdr.markForCheck();
    });
  }

  /**
   * Load UGM forecast data for next 7 days
   */
  private loadUgmForecastData(): void {
    const today = new Date();
    const requests = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD

      const request = this.ugmWeatherDataService.weatherUgmWeatherDataGetByCoordinatesAndDatePost({
        latitude: this.latitude!,
        longitude: this.longitude!,
        inputDate: dateStr
      });

      requests.push(
        request.pipe(
          catchError(error => {
            console.error(`Error loading UGM forecast for ${dateStr}:`, error);
            return of(null);
          })
        )
      );
    }

    forkJoin(requests).pipe(
      map(results => {
        this.forecastDays = results
          .filter(item => item !== null)
          .map((item: any) => {
            const tempMin = item.temperatureMin ?? item.temperature ?? 0;
            const tempMax = item.temperatureMax ?? item.temperature ?? 0;
            const avgTemp = (tempMin + tempMax) / 2;
            const weatherConditionKey = this.getWeatherCondition(
              item.fallout ?? item.precipitation ?? 0,
              avgTemp,
              item.humidity
            );

            return {
              date: item.date || item.observationDate,
              tempMin: Math.round(tempMin),
              tempMax: Math.round(tempMax),
              condition: this.translate.instant(weatherConditionKey),
              conditionKey: weatherConditionKey,
              precipitation: Math.round((item.fallout || item.precipitation || 0) * 10) / 10,
              humidity: Math.round(item.humidity || 0),
              windSpeed: Math.round((item.windSpeed || item.wind_speed || 0) * 10) / 10,
              windDirection: Math.round(item.windDirection || item.windDerection || 0),
              iconPath: this.getWeatherIconPath(weatherConditionKey)
            };
          });
        return results;
      }),
      catchError(error => {
        console.error('Error loading UGM forecast data:', error);
        this.isLoading = false;
        this.cdr.markForCheck();
        return of(null);
      })
    ).subscribe(() => {
      this.isLoading = false;
      this.scheduleForecastNavigationUpdate();
      this.cdr.markForCheck();
    });
  }

  /**
   * Load IBM forecast data for next 7 days
   */
  private loadIbmForecastData(): void {
    const today = new Date();
    const requests = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD

      const request = this.ibmDayWeatherForecastService.weatherReportsIbmDayWeatherForecastGetByCoordinatesAndDatePost({
        latitude: this.latitude!,
        longitude: this.longitude!,
        inputDate: dateStr
      });

      requests.push(
        request.pipe(
          catchError(error => {
            console.error(`Error loading IBM forecast for ${dateStr}:`, error);
            return of(null);
          })
        )
      );
    }

    forkJoin(requests).pipe(
      map(results => {
        this.forecastDays = results
          .filter(item => item !== null)
          .map((item: any) => {
            const tempMin = item.temperatureMin ?? item.temperature ?? 0;
            const tempMax = item.temperatureMax ?? item.temperature ?? 0;
            const avgTemp = (tempMin + tempMax) / 2;
            const weatherConditionKey = this.getWeatherCondition(
              item.fallout ?? item.precipitation ?? 0,
              avgTemp,
              item.humidity
            );

            return {
              date: item.date || item.forecastDate,
              tempMin: Math.round(tempMin),
              tempMax: Math.round(tempMax),
              condition: this.translate.instant(weatherConditionKey),
              conditionKey: weatherConditionKey,
              precipitation: Math.round((item.fallout || item.precipitation || 0) * 10) / 10,
              humidity: Math.round(item.humidity || 0),
              windSpeed: Math.round((item.windSpeed || item.wind_speed || 0) * 10) / 10,
              windDirection: Math.round(item.windDirection || item.windDerection || 0),
              iconPath: this.getWeatherIconPath(weatherConditionKey)
            };
          });
        return results;
      }),
      catchError(error => {
        console.error('Error loading IBM forecast data:', error);
        this.isLoading = false;
        this.cdr.markForCheck();
        return of(null);
      })
    ).subscribe(() => {
      this.isLoading = false;
      this.scheduleForecastNavigationUpdate();
      this.cdr.markForCheck();
    });
  }


  private getWeatherCondition(precipitation: number, temperature?: number, humidity?: number): string {
    // Heavy precipitation conditions
    if (precipitation > 20) {
      // Check if it's snow based on temperature
      if (temperature !== undefined && temperature < 0) {
        return 'heavy_snow'; // Kuchli qor / qor bo'roni
      }
      return 'heavy_rain'; // Kuchli yomg'ir
    }

    // Moderate to heavy rain/snow
    if (precipitation > 10) {
      if (temperature !== undefined && temperature < 0) {
        return 'snow'; // Qor
      }
      return 'heavy_rain'; // Kuchli yomg'ir
    }

    // Moderate rain
    if (precipitation > 5) {
      if (temperature !== undefined && temperature < 2 && temperature > -2) {
        return 'freezing_rain_sleet'; // Muzli yomg'ir / yomg'ir-qor aralash
      }
      return 'rain'; // Yomg'ir
    }

    // Light rain/drizzle
    if (precipitation > 2) {
      if (temperature !== undefined && temperature < 2) {
        return 'drizzle_freezing'; // Mayda yomg'ir / muzli mayda yomg'ir
      }
      return 'drizzle_freezing'; // Mayda yomg'ir
    }

    // Very light precipitation
    if (precipitation > 0.5) {
      return 'partly_cloudy_places'; // Ba'zi joylarda qisman bulutli
    }

    // Minimal precipitation
    if (precipitation > 0) {
      return 'partly_cloudy_places'; // Ba'zi joylarda qisman bulutli
    }

    // No precipitation - check humidity and other conditions
    if (humidity !== undefined) {
      if (humidity > 90) {
        return 'fog'; // Tuman
      }
      if (humidity > 70) {
        return 'cloudy'; // Bulutli ob-havo
      }
      if (humidity > 50) {
        return 'partly_cloudy_places'; // Ba'zi joylarda qisman bulutli
      }
    }

    // Clear weather
    return 'clear'; // Ochiq
  }

  /**
   * Scroll forecast carousel
   */
  scrollForecast(direction: 'prev' | 'next'): void {
    if (!this.forecastScrollElement) return;

    const scrollContainer = this.forecastScrollElement.nativeElement;
    const scrollAmount = 350;

    if (direction === 'prev') {
      scrollContainer.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      scrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }

    // Refresh arrow visibility after smooth scroll finishes.
    setTimeout(() => this.updateForecastNavigationVisibility(), 250);
  }

  onForecastScroll(): void {
    this.updateForecastNavigationVisibility();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateForecastNavigationVisibility();
  }

  private scheduleForecastNavigationUpdate(): void {
    setTimeout(() => {
      this.updateForecastNavigationVisibility();
      this.cdr.markForCheck();
    }, 0);
  }

  private updateForecastNavigationVisibility(): void {
    const scrollContainer = this.forecastScrollElement?.nativeElement as HTMLElement | undefined;
    if (!scrollContainer) {
      this.showForecastPrev = false;
      this.showForecastNext = false;
      return;
    }

    const maxScrollLeft = Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0);
    const currentScrollLeft = scrollContainer.scrollLeft;
    const canScroll = maxScrollLeft > 2;

    this.showForecastPrev = canScroll && currentScrollLeft > 2;
    this.showForecastNext = canScroll && currentScrollLeft < (maxScrollLeft - 2);
  }

  /**
   * Get weather icon path
   */
  getWeatherIconPath(iconName: string): string {
    const FALLBACK = '/assets/icons/main/light/weather/clear.svg';

    if (!iconName) return FALLBACK;

    // Normalize the input
    const normalized = iconName.toLowerCase().trim();

    // Map weather conditions to actual icons in /assets/icons/main/light/weather/
    const iconMap: Record<string, string> = {
      'sunrise': '/assets/icons/main/light/weather/sunrise.svg',
      'sunset': '/assets/icons/main/light/weather/sunset.svg',
      'clear': '/assets/icons/main/light/weather/clear.svg',
      'partly_cloudy_places': '/assets/icons/main/light/weather/partly_cloudy.svg',
      'haze': '/assets/icons/main/light/weather/haze.svg',
      'fog': '/assets/icons/main/light/weather/fog.svg',
      'windy': '/assets/icons/main/light/weather/windy.svg',
      'cloudy': '/assets/icons/main/light/weather/cloudy.svg',
      'thunderstorm': '/assets/icons/main/light/weather/thunderstorm.svg',
      'rain': '/assets/icons/main/light/weather/rain.svg',
      'heavy_rain': '/assets/icons/main/light/weather/heavy_rain.svg',
      'drizzle_freezing': '/assets/icons/main/light/weather/drizzle_freezing.svg',
      'snow': '/assets/icons/main/light/weather/snow.svg',
      'heavy_snow': '/assets/icons/main/light/weather/heavy_snow.svg',
      'freezing_rain_sleet': '/assets/icons/main/light/weather/freezing_rain_sleet.svg',
      'clear_night': '/assets/icons/main/light/weather/clear_night.svg',
      'partly_cloudy_night': '/assets/icons/main/light/weather/partly_cloudy_night.svg',
      'drizzle_night': '/assets/icons/main/light/weather/drizzle_night.svg'
    };

    // Return mapped icon or fallback
    return iconMap[normalized] || FALLBACK;
  }


}
